import { useEffect } from 'react';
import {
  BrowserRouter as Router,
  Routes,
  Route,
  Navigate,
} from 'react-router-dom';
import { Provider, useDispatch } from 'react-redux';
import { Box, CircularProgress, Typography } from '@mui/material';
import { store } from './store';
import { AppProvider } from './providers';
import { Layout, PermissionRoute } from './components';
import { useAuth } from './hooks/useReduxAuth';
import { initializeAuth } from './store/slices/authSlice';
import { SCREENS, ACTIONS } from './constants/permissions';
import {
  HomePage,
  CompaniesPage,
  UsersPage,
  GroupsPage,
  TeamsPage,
  TeamMembersPage,
  LoginPage,
  SettingsPage,
  ProfilePage,
  RoleManagementPage,
  TitlesPage,
  PartnerManagementPage,
  SektorsPage,
  NotFoundPage,
  DynamicReportsPage,
  JobDistributionPage,
} from './pages';
import LeaveRequestPrintPage from './pages/ApplicationsPage/LeaveRequestsPage/LeaveRequestPrintPage';
import ContractSubjectsPage from './pages/SettingsPage/ContractSubjectsPage';
import JobTypesPage from './pages/SettingsPage/JobTypesPage';
import RevenueCalculationRulesPage from './pages/SettingsPage/RevenueCalculationRulesPage';
import ScopeOfWorkPage from './pages/SettingsPage/ScopeOfWorkPage';
import PricingMethodsPage from './pages/SettingsPage/PricingMethodsPage';
import WorkStatusPage from './pages/SettingsPage/WorkStatusPage';
import ReportTypesPage from './pages/SettingsPage/ReportTypesPage';
import ProposalStatusPage from './pages/SettingsPage/ProposalStatusPage';
import TagsPage from './pages/SettingsPage/TagsPage';
import DocumentBankTagsPage from './pages/SettingsPage/DocumentBankTagsPage/DocumentBankTagsPage';
import ApplicationsPage from './pages/ApplicationsPage/ApplicationsPage';
import OrganizationHierarchy from './pages/SettingsPage/OrganizationHierarchy/OrganizationHierarchy';
import LeaveTypesPage from './pages/SettingsPage/LeaveTypesPage';
import ExpenseTypesPage from './pages/SettingsPage/ExpenseTypesPage';
import LeaveRequestsPage from './pages/ApplicationsPage/LeaveRequestsPage/LeaveRequestsPage';
import ExpensesPage from './pages/ApplicationsPage/ExpensesPage/ExpensesPage';
import UserFirmsPage from './pages/ApplicationsPage/UserFirmsPage/UserFirmsPage';
import UserFirmDetailPage from './pages/ApplicationsPage/UserFirmsPage/UserFirmDetailPage';
import FirmManagementPage from './pages/FirmManagementPage';
import FirmDetailPage from './pages/FirmManagementPage/FirmDetailPage/FirmDetailPage';
import CommunicationCardsPage from './pages/FirmManagementPage/CommunicationCardsPage';
import ContractsPage from './pages/FirmManagementPage/ContractsPage/ContractsPage';
import OffersPage from './pages/FirmManagementPage/OffersPage/OffersPage';
import JobManagementPage from './pages/JobManagementPage/JobManagementPage';
import TasksPage from './pages/TasksPage/TasksPage';
import JobAssignmentsPage from './pages/JobAssignmentsPage/JobAssignmentsPage';
import PaymentPlanTemplatesPage from './pages/SettingsPage/PaymentPlanTemplatesPage/PaymentPlanTemplatesPage';
import PricingProcessPage from './pages/SettingsPage/PricingProcessPage/PricingProcessPage';
import InvoiceManagementPage from './pages/InvoiceManagementPage';
import TahsilatPage from './pages/TahsilatPage/TahsilatPage';
import PrimCiroKurallariPage from './pages/PrimYonetimi/PrimCiroKurallariPage';
import PrimKurallariPage from './pages/PrimYonetimi/PrimKurallariPage';
import PrimTahsilatPage from './pages/PrimYonetimi/PrimTahsilatPage';
import BonusRevenueTypesPage from './pages/SettingsPage/BonusRevenueTypesPage';
import UserBonusCoefficientsPage from './pages/SettingsPage/UserBonusCoefficientsPage';
import BonusTargetsPage from './pages/PrimYonetimi/BonusTargetsPage';
import CiroEklePage from './pages/PrimManagementPage/CiroEklePage';
import CiroTalepleriPage from './pages/ApplicationsPage/CiroTalepleriPage';
import CiroAktarPage from './pages/ApplicationsPage/CiroAktarPage';
import InvoiceCodesPage from './pages/SettingsPage/InvoiceCodesPage';
import AlertsPage from './pages/AlertsPage';
import ReportManagementPage from './pages/ReportManagementPage';
import DocumentBankPage from './pages/DocumentBankPage/DocumentBankPage';
import './locales/i18n';

const LoadingScreen = () => (
  <Box
    sx={{
      display: 'flex',
      flexDirection: 'column',
      justifyContent: 'center',
      alignItems: 'center',
      minHeight: '100vh',
      gap: 2,
    }}
  >
    <CircularProgress size={50} />
    <Typography variant="h6" color="text.secondary">
      Yükleniyor...
    </Typography>
  </Box>
);

const ProtectedRoute = ({ children }) => {
  const { isAuthenticated } = useAuth();
  return isAuthenticated ? children : <Navigate to="/login" replace />;
};

const AppContent = () => {
  const { isAuthenticated } = useAuth();

  return (
    <Routes>
      <Route
        path="/login"
        element={isAuthenticated ? <Navigate to="/" replace /> : <LoginPage />}
      />
      {/* İzin yazdırma sayfası - Layout olmadan, sadece form */}
      <Route
        path="/applications/leave-requests/print/:id"
        element={
          <PermissionRoute action={ACTIONS.VIEW}>
            <LeaveRequestPrintPage />
          </PermissionRoute>
        }
      />
      <Route
        path="/*"
        element={
          <ProtectedRoute>
            <Layout>
              <Routes>
                <Route path="/" element={<HomePage />} />
                <Route
                  path="/companies"
                  element={
                    <PermissionRoute
                      screen={SCREENS.COMPANIES}
                      action={ACTIONS.VIEW}
                    >
                      <CompaniesPage />
                    </PermissionRoute>
                  }
                />
                <Route
                  path="/users"
                  element={
                    <PermissionRoute
                      screen={SCREENS.USERS}
                      action={ACTIONS.VIEW}
                    >
                      <UsersPage />
                    </PermissionRoute>
                  }
                />
                <Route
                  path="/groups"
                  element={
                    <PermissionRoute
                      screen={SCREENS.GROUPS}
                      action={ACTIONS.VIEW}
                    >
                      <GroupsPage />
                    </PermissionRoute>
                  }
                />
                <Route
                  path="/teams"
                  element={
                    <PermissionRoute
                      screen={SCREENS.TEAMS}
                      action={ACTIONS.VIEW}
                    >
                      <TeamsPage />
                    </PermissionRoute>
                  }
                />
                <Route
                  path="/teams/:teamId/members"
                  element={
                    <PermissionRoute
                      screen={SCREENS.TEAM_MEMBERS}
                      action={ACTIONS.VIEW}
                    >
                      <TeamMembersPage />
                    </PermissionRoute>
                  }
                />
                <Route path="/applications" element={<ApplicationsPage />} />
                <Route
                  path="/applications/leave-requests"
                  element={
                    <PermissionRoute
                      screen={SCREENS.LEAVE_REQUESTS}
                      action={ACTIONS.VIEW}
                      requiresLRM={true}
                    >
                      <LeaveRequestsPage />
                    </PermissionRoute>
                  }
                />
                <Route
                  path="/applications/expenses"
                  element={
                    <PermissionRoute requiresEM={true}>
                      <ExpensesPage />
                    </PermissionRoute>
                  }
                />
                <Route
                  path="/applications/dynamic-reports"
                  element={
                    <PermissionRoute requiresRoot>
                      <DynamicReportsPage />
                    </PermissionRoute>
                  }
                />
                <Route
                  path="/applications/user-firms"
                  element={
                    <PermissionRoute
                      screen={SCREENS.USERS}
                      action={ACTIONS.VIEW}
                    >
                      <UserFirmsPage />
                    </PermissionRoute>
                  }
                />
                <Route
                  path="/applications/user-firms/:userId"
                  element={
                    <PermissionRoute
                      screen={SCREENS.USERS}
                      action={ACTIONS.VIEW}
                    >
                      <UserFirmDetailPage />
                    </PermissionRoute>
                  }
                />
                <Route
                  path="/firm-management"
                  element={
                    <PermissionRoute
                      screen={SCREENS.FIRM_MANAGEMENTS}
                      action={ACTIONS.VIEW}
                    >
                      <FirmManagementPage />
                    </PermissionRoute>
                  }
                />
                <Route
                  path="/firm-management/:firmId"
                  element={
                    <PermissionRoute
                      screen={SCREENS.FIRM_MANAGEMENTS}
                      action={ACTIONS.VIEW}
                    >
                      <FirmDetailPage />
                    </PermissionRoute>
                  }
                />
                <Route
                  path="/firm-management/:firmId/cards"
                  element={
                    <PermissionRoute>
                      <CommunicationCardsPage />
                    </PermissionRoute>
                  }
                />
                <Route
                  path="/contracts"
                  element={
                    <PermissionRoute
                      screen={SCREENS.CONTRACTS}
                      action={ACTIONS.VIEW}
                    >
                      <ContractsPage />
                    </PermissionRoute>
                  }
                />
                <Route
                  path="/offers"
                  element={
                    <PermissionRoute
                      screen={SCREENS.OFFERS}
                      action={ACTIONS.VIEW}
                    >
                      <OffersPage />
                    </PermissionRoute>
                  }
                />
                <Route
                  path="/job-management"
                  element={
                    <PermissionRoute
                      screen={SCREENS.JOB_MANAGEMENT}
                      action={ACTIONS.VIEW}
                    >
                      <JobManagementPage />
                    </PermissionRoute>
                  }
                />
                <Route
                  path="/is-dagilimlari"
                  element={
                    <PermissionRoute
                      screen={SCREENS.JOB_DISTRIBUTIONS}
                      action={ACTIONS.VIEW}
                    >
                      <JobDistributionPage />
                    </PermissionRoute>
                  }
                />
                <Route
                  path="/tasks"
                  element={
                    <PermissionRoute
                      screen={SCREENS.TASKS}
                      action={ACTIONS.VIEW}
                    >
                      <TasksPage />
                    </PermissionRoute>
                  }
                />
                <Route
                  path="/job-assignments"
                  element={
                    <PermissionRoute
                      screen={SCREENS.JOB_ASSIGNMENTS}
                      action={ACTIONS.VIEW}
                    >
                      <JobAssignmentsPage />
                    </PermissionRoute>
                  }
                />
                <Route
                  path="/report-management"
                  element={
                    <PermissionRoute
                      screen={SCREENS.REPORT_MANAGEMENT}
                      action={ACTIONS.VIEW}
                    >
                      <ReportManagementPage view="list" />
                    </PermissionRoute>
                  }
                />
                <Route
                  path="/report-management/flows"
                  element={
                    <PermissionRoute
                      screen={SCREENS.REPORT_MANAGEMENT}
                      action={ACTIONS.VIEW}
                    >
                      <ReportManagementPage view="flows" />
                    </PermissionRoute>
                  }
                />
                <Route
                  path="/invoice-management"
                  element={
                    <PermissionRoute
                      screen={SCREENS.INVOICE_MANAGEMENT}
                      action={ACTIONS.VIEW}
                    >
                      <InvoiceManagementPage />
                    </PermissionRoute>
                  }
                />
                <Route
                  path="/applications/tahsilat"
                  element={
                    <PermissionRoute requiresCM={true}>
                      <TahsilatPage />
                    </PermissionRoute>
                  }
                />
                <Route
                  path="/prim-management"
                  element={
                    <PermissionRoute
                      screen={SCREENS.PRIM_MANAGEMENT}
                      action={ACTIONS.VIEW}
                    >
                      <PrimCiroKurallariPage />
                    </PermissionRoute>
                  }
                />
                <Route
                  path="/ciro-kurallari"
                  element={
                    <PermissionRoute
                      screen={SCREENS.PRIM_MANAGEMENT}
                      action={ACTIONS.VIEW}
                    >
                      <PrimCiroKurallariPage />
                    </PermissionRoute>
                  }
                />
                <Route
                  path="/kurallar"
                  element={
                    <PermissionRoute
                      screen={SCREENS.PRIM_MANAGEMENT}
                      action={ACTIONS.VIEW}
                    >
                      <PrimKurallariPage />
                    </PermissionRoute>
                  }
                />
                <Route
                  path="/tahsilat"
                  element={
                    <PermissionRoute
                      screen={SCREENS.PRIM_MANAGEMENT}
                      action={ACTIONS.VIEW}
                    >
                      <PrimTahsilatPage />
                    </PermissionRoute>
                  }
                />
                <Route
                  path="/settings/bonus-revenue-types"
                  element={
                    <PermissionRoute>
                      <BonusRevenueTypesPage />
                    </PermissionRoute>
                  }
                />
                <Route
                  path="/settings/kullanici-prim-katsayilari"
                  element={
                    <PermissionRoute
                      screen={SCREENS.SETTINGS}
                      action={ACTIONS.VIEW}
                    >
                      <UserBonusCoefficientsPage />
                    </PermissionRoute>
                  }
                />
                <Route
                  path="/hedefler"
                  element={
                    <PermissionRoute
                      screen={SCREENS.PRIM_MANAGEMENT}
                      action={ACTIONS.VIEW}
                    >
                      <BonusTargetsPage />
                    </PermissionRoute>
                  }
                />
                <Route
                  path="/ciro-ekle"
                  element={
                    <PermissionRoute
                      screen={SCREENS.PRIM_MANAGEMENT}
                      action={ACTIONS.VIEW}
                    >
                      <CiroEklePage />
                    </PermissionRoute>
                  }
                />
                <Route path="/applications/ciro-talepleri" element={<CiroTalepleriPage />} />
                <Route
                  path="/ciro-aktar"
                  element={
                    <PermissionRoute
                      screen={SCREENS.PRIM_MANAGEMENT}
                      action={ACTIONS.VIEW}>
                      <CiroAktarPage />
                    </PermissionRoute>
                  }
                />
                <Route path="/alerts" element={<AlertsPage />} />
                <Route
                  path="/document-bank"
                  element={
                      <DocumentBankPage />
                  }
                />
                <Route
                  path="/settings"
                  element={
                    <PermissionRoute
                      screen={SCREENS.SETTINGS}
                      action={ACTIONS.VIEW}
                    >
                      <SettingsPage />
                    </PermissionRoute>
                  }
                />
                <Route
                  path="/settings/organization-hierarchy"
                  element={
                    <PermissionRoute
                      requiresRoot={true}
                      screen={SCREENS.SETTINGS}
                      action={ACTIONS.VIEW}
                    >
                      <OrganizationHierarchy />
                    </PermissionRoute>
                  }
                />
                <Route
                  path="/settings/titles"
                  element={
                    <PermissionRoute
                      screen={SCREENS.SETTINGS}
                      action={ACTIONS.VIEW}
                    >
                      <TitlesPage />
                    </PermissionRoute>
                  }
                />
                <Route
                  path="/settings/role-management"
                  element={
                    <PermissionRoute requiresRoot={true}>
                      <RoleManagementPage />
                    </PermissionRoute>
                  }
                />
                <Route
                  path="/settings/leave-types"
                  element={
                    <PermissionRoute
                      screen={SCREENS.SETTINGS}
                      action={ACTIONS.VIEW}
                    >
                      <LeaveTypesPage />
                    </PermissionRoute>
                  }
                />
                <Route
                  path="/settings/expense-types"
                  element={
                    <PermissionRoute
                      screen={SCREENS.SETTINGS}
                      action={ACTIONS.VIEW}
                    >
                      <ExpenseTypesPage />
                    </PermissionRoute>
                  }
                />
                <Route
                  path="/settings/partner-management"
                  element={
                    <PermissionRoute
                      screen={SCREENS.SETTINGS}
                      action={ACTIONS.VIEW}
                    >
                      <PartnerManagementPage />
                    </PermissionRoute>
                  }
                />
                <Route
                  path="/settings/sectors"
                  element={
                    <PermissionRoute
                      screen={SCREENS.SETTINGS}
                      action={ACTIONS.VIEW}
                    >
                      <SektorsPage />
                    </PermissionRoute>
                  }
                />
                <Route
                  path="/settings/contract-subjects"
                  element={
                    <PermissionRoute
                      screen={SCREENS.SETTINGS}
                      action={ACTIONS.VIEW}
                    >
                      <ContractSubjectsPage />
                    </PermissionRoute>
                  }
                />
                <Route
                  path="/settings/job-types"
                  element={
                    <PermissionRoute
                      screen={SCREENS.SETTINGS}
                      action={ACTIONS.VIEW}
                    >
                      <JobTypesPage />
                    </PermissionRoute>
                  }
                />
                <Route
                  path="/settings/revenue-calculation-rules"
                  element={
                    <PermissionRoute
                      screen={SCREENS.SETTINGS}
                      action={ACTIONS.VIEW}
                    >
                      <RevenueCalculationRulesPage />
                    </PermissionRoute>
                  }
                />
                <Route
                  path="/settings/scope-of-work"
                  element={
                    <PermissionRoute
                      screen={SCREENS.SETTINGS}
                      action={ACTIONS.VIEW}
                    >
                      <ScopeOfWorkPage />
                    </PermissionRoute>
                  }
                />
                <Route
                  path="/settings/pricing-methods"
                  element={
                    <PermissionRoute
                      screen={SCREENS.SETTINGS}
                      action={ACTIONS.VIEW}
                    >
                      <PricingMethodsPage />
                    </PermissionRoute>
                  }
                />
                <Route
                  path="/settings/work-status"
                  element={
                    <PermissionRoute
                      screen={SCREENS.SETTINGS}
                      action={ACTIONS.VIEW}
                    >
                      <WorkStatusPage />
                    </PermissionRoute>
                  }
                />
                <Route
                  path="/settings/report-types"
                  element={
                    <PermissionRoute
                      screen={SCREENS.SETTINGS}
                      action={ACTIONS.VIEW}
                    >
                      <ReportTypesPage />
                    </PermissionRoute>
                  }
                />
                <Route
                  path="/settings/proposal-statuses"
                  element={
                    <PermissionRoute
                      screen={SCREENS.SETTINGS}
                      action={ACTIONS.VIEW}
                    >
                      <ProposalStatusPage />
                    </PermissionRoute>
                  }
                />
                <Route
                  path="/settings/tags"
                  element={
                    <PermissionRoute
                      screen={SCREENS.SETTINGS}
                      action={ACTIONS.VIEW}
                    >
                      <TagsPage />
                    </PermissionRoute>
                  }
                />
                <Route
                  path="/settings/document-bank-tags"
                  element={
                    <PermissionRoute
                      screen={SCREENS.SETTINGS}
                      action={ACTIONS.VIEW}
                    >
                      <DocumentBankTagsPage />
                    </PermissionRoute>
                  }
                />
                <Route
                  path="/settings/payment-plan-templates"
                  element={
                    <PermissionRoute
                      screen={SCREENS.SETTINGS}
                      action={ACTIONS.VIEW}
                    >
                      <PaymentPlanTemplatesPage />
                    </PermissionRoute>
                  }
                />
                <Route
                  path="/settings/pricing-process"
                  element={
                    <PermissionRoute
                      screen={SCREENS.SETTINGS}
                      action={ACTIONS.VIEW}
                    >
                      <PricingProcessPage />
                    </PermissionRoute>
                  }
                />
                <Route
                  path="/settings/invoice-codes"
                  element={
                    <PermissionRoute
                      screen={SCREENS.SETTINGS}
                      action={ACTIONS.VIEW}
                    >
                      <InvoiceCodesPage />
                    </PermissionRoute>
                  }
                />
                <Route path="/profile" element={<ProfilePage />} />
                <Route path="*" element={<NotFoundPage />} />
              </Routes>
            </Layout>
          </ProtectedRoute>
        }
      />
    </Routes>
  );
};

const AuthInitializer = ({ children }) => {
  const dispatch = useDispatch();
  const { isInitialized } = useAuth();

  useEffect(() => {
    dispatch(initializeAuth());
  }, [dispatch]);

  if (!isInitialized) {
    return <LoadingScreen />;
  }

  return children;
};

const App = () => {
  return (
    <Provider store={store}>
      <AppProvider>
        <Router>
          <AuthInitializer>
            <AppContent />
          </AuthInitializer>
        </Router>
      </AppProvider>
    </Provider>
  );
};

export default App;
